// 仕入発注リソースの宣言。既存の procurement.service をそのまま呼ぶ（ブラウザ側＝本人の権限で実行）。
//
// 【業務フロー】仕入発注は仕入先（取引先マスタの supplier 区分）を指定して作成する。
// 案件（orderCode）は任意。金額は明細から計算し、集計同期（order.procurementTotal）は本体サービスの責務。
// ※ 仕入の明細単価は本体スキーマが .min(0)（負値不可）。AI経路でも最終検証は本体 zod が行う（多層防御）。
import { z } from 'zod';
import { Timestamp } from 'firebase/firestore';
import {
  fetchProcurementOrders,
  createProcurementOrder,
  updateProcurementOrder,
  type ProcurementOrderCreateInput,
  type ProcurementOrderUpdateInput,
} from '@modules/procurement/services/procurement.service';
import type { ProcurementOrder } from '@modules/procurement/types';
import { findOrderByCode } from './order.resource';
import type { ID } from '@shared/types';
import type { ListFilters, ResourceDescriptor, WriteResult } from './types';
import {
  MAX_ROWS,
  aiDocItemSchema,
  calcDocTotals,
  dateString,
  normalizeCode,
  resolveCustomer,
  resolveEmployee,
  toDateString,
  toDocLineItems,
  toTimestamp,
} from './shared';

/** 仕入発注1件を、LLM が読む最小限の形へ射影する。 */
function toCompactProcurementOrder(p: ProcurementOrder) {
  return {
    code: p.code,
    title: p.title,
    total: p.total, // 税込合計
    orderDate: toDateString(p.orderDate),
  };
}

async function list(filters: ListFilters): Promise<unknown> {
  const orders = await fetchProcurementOrders();

  const keyword = filters.keyword;
  const filtered = keyword
    ? orders.filter((p) => p.title.includes(keyword) || p.code.includes(keyword))
    : orders;

  return {
    count: filtered.length,
    truncated: filtered.length > MAX_ROWS,
    procurement_orders: filtered.slice(0, MAX_ROWS).map(toCompactProcurementOrder),
  };
}

async function get(rawCode: string): Promise<unknown> {
  const code = normalizeCode(rawCode);
  const orders = await fetchProcurementOrders();
  const found = orders.find((p) => normalizeCode(p.code) === code);
  if (!found) {
    return { found: false, note: `仕入発注番号「${rawCode}」は見つかりませんでした` };
  }
  return { found: true, procurement_order: toCompactProcurementOrder(found) };
}

// ── 書込（C-2）─────────────────────────────────────────────────

const writeCreateSchema = z.object({
  // 【原則】Firestore の ID を LLM に組ませない。名前か顧客コードを受け、ツール側で解決する
  supplier: z.string().min(1, 'supplier（仕入先の名前または顧客コード）は必須'),
  title: z.string().min(1, 'title（件名）は必須'),
  orderCode: z.string().optional(),
  items: z.array(aiDocItemSchema).min(1).max(30),
  notes: z.string().optional(),
  assignee: z.string().min(1).optional(),
  orderDate: dateString.optional(),
  expectedDate: dateString.optional(),
});

const writeUpdateSchema = z
  .object({
    supplier: z.string().min(1).optional(),
    title: z.string().min(1).optional(),
    notes: z.string().optional(),
    assignee: z.string().min(1).optional(),
    orderDate: dateString.optional(),
    expectedDate: dateString.optional(),
    /** 渡すと明細を「全て置き換える」（追記ではない）。金額は自動再計算される。 */
    items: z.array(aiDocItemSchema).min(1).max(30).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'changes に変更する項目を1つ以上入れる' });

/** code から更新対象の仕入発注を引き当てる（get と同じ正規化・完全一致のみ）。 */
async function findByCode(rawCode: string): Promise<ProcurementOrder | null> {
  const code = normalizeCode(rawCode);
  const orders = await fetchProcurementOrders();
  return orders.find((p) => normalizeCode(p.code) === code) ?? null;
}

export const procurementOrderResource: ResourceDescriptor = {
  name: 'procurement_order',
  label: '仕入発注',
  list,
  get,
  write: {
    fields: [
      { name: 'supplier', label: '仕入先', type: 'string', required: true, note: '仕入先の名前または顧客コードをそのまま渡す。IDは不要' },
      { name: 'title', label: '件名', type: 'string', required: true },
      { name: 'orderCode', label: '案件番号', type: 'ref', resource: 'order', note: '作成時のみ・案件番号（任意）' },
      { name: 'items', label: '明細', type: 'items', required: true, note: '配列 [{name,quantity,unit?,unitPrice}]。更新では全置き換え・金額は自動計算' },
      { name: 'notes', label: '備考', type: 'string' },
      { name: 'assignee', label: '担当者', type: 'string', note: '担当者名で指定（IDは不要）' },
      { name: 'orderDate', label: '発注日', type: 'date', note: 'YYYY-MM-DD' },
      { name: 'expectedDate', label: '納品予定日', type: 'date', note: 'YYYY-MM-DD' },
    ],
    createSchema: writeCreateSchema,
    // 支払（paidAt/paymentDate/billId）・status は画面の責務。AIには開放しない
    updateSchema: writeUpdateSchema,
    async create(data, userId: ID): Promise<WriteResult> {
      const input = data as z.infer<typeof writeCreateSchema>;

      // ① 仕入先を名前/コードから解決（存在しないIDでの作成を許さない）
      const supplier = await resolveCustomer(input.supplier, 'supplier');

      // ② 案件は任意。指定されたら解決する（存在しない番号での作成は許さない）
      const order = input.orderCode ? await findOrderByCode(input.orderCode) : null;
      if (input.orderCode && !order) {
        throw new Error(
          `案件「${input.orderCode}」が見つかりません。先に create_record(order) で受注を作成し、その code を orderCode に指定してください`
        );
      }

      // ③ 金額は明細から計算（税計算モードは案件から継承。案件なしは floor）
      const taxCalcMode = order?.taxCalcMode ?? 'floor';
      const totals = calcDocTotals(input.items, taxCalcMode);

      // ④ 作成（採番・監査ログ・受注集計の同期は本体サービスの責務）
      const created = await createProcurementOrder(
        {
          supplierId: supplier.id,
          title: input.title,
          orderId: order?.id ?? null,
          // 担当者は名前→ID解決。指定が無ければ実行者を自動設定する（人がレビュー時に変更できる）
          assignedUserId: input.assignee !== undefined ? (await resolveEmployee(input.assignee)).id : userId,
          ...(input.expectedDate !== undefined ? { expectedDate: toTimestamp(input.expectedDate) } : {}),
          amount: totals.subtotal,
          taxAmount: totals.taxAmount,
          total: totals.total,
          // 帳票表示・受注集計（grandtotal 基準）にも同じ金額を渡す
          subtotal: totals.subtotal,
          totalgst: totals.taxAmount,
          grandtotal: totals.total,
          orderDate: input.orderDate !== undefined ? toTimestamp(input.orderDate) : Timestamp.now(),
          lineItems: toDocLineItems(input.items),
          taxCalcMode,
          ...(input.notes !== undefined ? { notes: input.notes } : {}),
          aiPendingReview: true,
        } as ProcurementOrderCreateInput,
        userId
      );
      return { id: created.id, code: created.code };
    },
    async update(code, changes, userId: ID): Promise<WriteResult | null> {
      const target = await findByCode(code);
      if (!target) return null;
      const input = changes as z.infer<typeof writeUpdateSchema>;

      const patch: ProcurementOrderUpdateInput = { aiPendingReview: true };
      // 仕入先・担当者は名前→ID解決（IDをLLMに組ませない）
      if (input.supplier !== undefined) {
        patch.supplierId = (await resolveCustomer(input.supplier, 'supplier')).id;
      }
      if (input.assignee !== undefined) patch.assignedUserId = (await resolveEmployee(input.assignee)).id;
      if (input.title !== undefined) patch.title = input.title;
      if (input.notes !== undefined) patch.notes = input.notes;
      if (input.orderDate !== undefined) patch.orderDate = toTimestamp(input.orderDate);
      if (input.expectedDate !== undefined) patch.expectedDate = toTimestamp(input.expectedDate);

      // 明細は「全置き換え」。金額は明細から再計算する（create と同じ対応で
      // amount/taxAmount/total と subtotal/totalgst/grandtotal の両系列へ渡す）
      if (input.items && input.items.length > 0) {
        const taxCalcMode = target.taxCalcMode ?? 'floor';
        const totals = calcDocTotals(input.items, taxCalcMode);
        patch.lineItems = toDocLineItems(input.items);
        patch.amount = totals.subtotal;
        patch.taxAmount = totals.taxAmount;
        patch.total = totals.total;
        patch.subtotal = totals.subtotal;
        patch.totalgst = totals.taxAmount;
        patch.grandtotal = totals.total;
      }

      // 受注集計（order.procurementTotal）の同期は本体サービスの責務
      await updateProcurementOrder(target.id, patch, userId);
      return { id: target.id, code: target.code };
    },
    link: (id) => `/procurement/${id}/edit`,
  },
};
